import { cn } from "@/shared/lib/cn";

/**
 * Model origins that ship a brand mark in `/public/pi-color/<origin>.svg`
 * (colored where the brand has color). Anything outside this set falls back
 * to the origin's first letter.
 */
const KNOWN_ORIGINS = new Set([
  "openai",
  "anthropic",
  "google",
  "deepseek",
  "xai",
  "meta",
  "alibaba",
  "moonshot",
  "z-ai",
  "minimax",
  "black-forest-labs",
  "bytedance",
  "kwaivgi",
  "qwen",
  "fal",
]);

export interface ProviderIconProps {
  /** The model's `origin` (e.g. "openai", "anthropic", "black-forest-labs"). */
  origin: string;
  className?: string;
}

/* Brands whose official mark is monochrome — inverted in dark so they stay visible. */
const MONO_ORIGINS = new Set(["openai", "xai", "moonshot", "black-forest-labs", "fal"]);

/**
 * A model provider's brand mark in its real colors (`/public/pi-color/`).
 * Mono-brand marks (OpenAI, xAI, …) invert in dark mode. Unknown origins fall
 * back to a bold first-letter glyph, so the catalog can add providers before
 * their mark lands. Size via `className` (e.g. `size-4`).
 */
export function ProviderIcon({ origin, className }: ProviderIconProps) {
  const key = origin.toLowerCase().trim();

  if (!KNOWN_ORIGINS.has(key)) {
    return (
      <span
        aria-hidden="true"
        className={cn(
          "inline-flex items-center justify-center text-[0.62em] font-bold uppercase leading-none",
          className,
        )}
      >
        {key[0] ?? "?"}
      </span>
    );
  }

  return (
    <img
      src={`/pi-color/${key}.svg`}
      alt={origin}
      className={cn("inline-block shrink-0 select-none", MONO_ORIGINS.has(key) && "dark:invert", className)}
      draggable={false}
    />
  );
}
